import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
	OnDestroy,
	ChangeDetectionStrategy,
} from '@angular/core';
import { NotificationsStore } from './store/notifications.store';
import { selectSession } from '@state/session';
import { map, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NotificationDTO, RestService, WEB_SOCKET_NOTIFICATION_TYPES } from 'cargo-chief-commons';
import { Router } from '@angular/router';
import { UrlService } from '@service/url/url.service';
import { CarrierService } from '@service/carrier/carrier.service';
import { BaseComponent } from '@shared/base/base.component';

const redirectMappings = {
	RFP: '/background-jobs',
};

@Component({
	selector: 'app-notifications',
	templateUrl: './notifications.component.html',
	styleUrls: ['./notifications.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent extends BaseComponent implements OnInit, OnDestroy {
	unreadCount$ = this.notificationsStore.unread$.pipe(
		takeUntil(this.isDestroyed$),
		map((unread) => {
			if (unread > 99) {
				return '99+';
			}
			if (unread === 0) {
				return null;
			}

			return unread;
		})
	);

	@ViewChild('notifications')
	notificationContainer: ElementRef;
	@ViewChildren('notification', { read: ElementRef })
	notifications: QueryList<ElementRef>;

	constructor(
		private readonly router: Router,
		private readonly store: Store,
		private readonly urlService: UrlService,
		private readonly restService: RestService,
		private readonly ref: ChangeDetectorRef,
		private readonly carrierService: CarrierService,
		public notificationsStore: NotificationsStore
	) {
		super();
	}

	ngOnInit(): void {
		let userId;

		this.store
			.select(selectSession)
			.pipe(take(1), takeUntil(this.isDestroyed$))
			.subscribe((session) => {
				userId = session.data.user.id;
			});

		this.notificationsStore.notifications$
			.pipe(take(1), takeUntil(this.isDestroyed$))
			.subscribe((notifications) => {
				if (!notifications?.length) this.notificationsStore.loadNotifications(userId);
			});
	}

	notificationClicked(notification: NotificationDTO): void {
		this.notificationsStore.removeNotification(notification);
		const isConfirmContactNotification =
			notification.type == WEB_SOCKET_NOTIFICATION_TYPES.INCORRECT_CONTACT;
		if (isConfirmContactNotification) {
			this.carrierService.confirmContact(notification);
		}

		this.restService
			.DELETE(this.urlService.deleteNotifications(), {
				ids: [notification.id],
			})
			.subscribe(() => {
				this.ref.markForCheck();
				if (!isConfirmContactNotification) {
					this.router.navigate([redirectMappings[notification.type]]);
				}
			});
	}

	getVisibleElements(limit: number): void {
		if (!this.notificationContainer?.nativeElement) return;

		const bottom = this.notificationContainer.nativeElement.getBoundingClientRect().bottom;

		let reads = 0;
		let ids = [];

		this.notifications
			.filter((notification) => {
				const rect = notification.nativeElement.getBoundingClientRect();
				return rect.bottom <= bottom;
			})
			.filter((notification) => {
				const notificationData = JSON.parse(
					notification.nativeElement.getAttribute('data-notification')
				);

				return !notificationData?.read && reads++ < limit;
			})
			.forEach((notification) => {
				const notificationData: NotificationDTO = JSON.parse(
					notification.nativeElement.getAttribute('data-notification')
				);
				this.notificationsStore.upsertNotification({ ...notificationData, read: true });
				ids.push(notificationData.id);
			});

		if (!ids.length) return;

		this.restService
			.PUT(this.urlService.updateNotifications(), {
				changes: { read: true },
				ids,
			})
			.subscribe();
	}

	serializeNotification(notification: NotificationDTO): string {
		return JSON.stringify(notification);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.notificationsStore.clear();
	}
}
